import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Edit3, Trash2, AlertCircle, Lock, Loader2 } from "lucide-react";
import ProfileFormModal from "./ProfileFormModal";
import profileService from "@/services/profileService";
import userService from "@/services/userService";
import menuService from "@/services/menuService";
import { showError, showInfo } from "@/store/errorStore";

/**
 * Erros HTTP já são exibidos pelo modal global (axiosInterceptor);
 * só mostramos aqui erros sem resposta do servidor (ex.: falha local).
 */
const showLocalError = (error, fallback) => {
  if (!error?.response && !error?.request) {
    showError(error?.message || fallback, "Erro");
  }
};

/**
 * Gerenciamento de perfis de acesso (bf_role) e menus de cada perfil
 */
export default function ProfilesManagement({
  profiles = [],
  menus = [],
  users = [],
  loading,
  isOwner,
  onReload,
}) {
  const [showModal, setShowModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingProfileId, setLoadingProfileId] = useState(null);
  const [deletingProfile, setDeletingProfile] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const menuLabels = menus.reduce((acc, menu) => {
    acc[menu.link] = menu.label;
    return acc;
  }, {});

  const countUsers = (profile) =>
    users.filter((user) => userService.hasRole(user, profile.name)).length;

  // Perfis são globais: criar, editar e remover é exclusivo do ROLE_OWNER
  const canEdit = () => !!isOwner;

  const getDeleteBlockReason = (profile) => {
    if (!isOwner) return "Somente o proprietário do sistema (ROLE_OWNER) pode alterar perfis";
    if (profile.system) return "Perfil do sistema não pode ser removido";
    if (countUsers(profile) > 0)
      return "Perfil vinculado a usuários não pode ser removido";
    return null;
  };

  const handleCreate = () => {
    setEditingProfile(null);
    setShowModal(true);
  };

  /**
   * Abrir modal de edição (busca dados atualizados por ID)
   */
  const handleEdit = async (profile) => {
    setLoadingProfileId(profile.id);
    try {
      const fresh = await profileService.getProfileById(profile.id);
      setEditingProfile(fresh || profile);
      setShowModal(true);
    } catch (error) {
      showLocalError(error, "Erro ao carregar perfil");
    } finally {
      setLoadingProfileId(null);
    }
  };

  const handleSave = async (profileData) => {
    setSaving(true);
    try {
      const label = profileService.getProfileLabel(profileData.name);
      if (editingProfile) {
        await profileService.updateProfile(editingProfile.id, profileData);
        showInfo(
          `Perfil "${label}" atualizado com sucesso`,
          "Perfil Atualizado",
        );
      } else {
        await profileService.createProfile(profileData);
        showInfo(`Perfil "${label}" criado com sucesso`, "Perfil Criado");
      }
      setShowModal(false);
      setEditingProfile(null);
      await onReload();
      menuService.notifyMenusChanged();
    } catch (error) {
      showLocalError(error, "Erro ao salvar perfil");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (profile) => {
    setDeletingProfile(profile);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingProfile) return;

    setDeleting(true);
    try {
      await profileService.deleteProfile(deletingProfile.id);
      showInfo(
        `Perfil "${profileService.getProfileLabel(
          deletingProfile.name,
        )}" removido com sucesso`,
        "Perfil Removido",
      );
      await onReload();
      menuService.notifyMenusChanged();
    } catch (error) {
      showLocalError(error, "Erro ao remover perfil");
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
      setDeletingProfile(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between space-y-0">
          <div>
            <CardTitle>Perfis de Acesso</CardTitle>
            <CardDescription>
              Defina quais menus cada perfil acessa. O perfil é vinculado ao
              usuário na aba "Usuários".
            </CardDescription>
          </div>
          {isOwner ? (
            <Button
              onClick={handleCreate}
              className="bg-[#1b5e20] hover:bg-[#144919] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Perfil
            </Button>
          ) : (
            <span className="text-sm text-gray-500">
              Somente consulta: perfis são alterados pelo proprietário do
              sistema
            </span>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p className="text-gray-600">Carregando perfis...</p>
              </div>
            </div>
          ) : profiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mb-3" />
              <p className="text-gray-600 font-medium">
                Nenhum perfil cadastrado
              </p>
              <p className="text-gray-500 text-sm mt-1">
                Clique em 'Novo Perfil' para começar
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Perfil</TableHead>
                    <TableHead>Menus</TableHead>
                    <TableHead>Usuários</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {profiles.map((profile) => {
                    const deleteBlock = getDeleteBlockReason(profile);
                    const profileMenus = profile.menus || [];
                    return (
                      <TableRow key={profile.id}>
                        <TableCell>
                          <div className="font-medium flex items-center gap-2">
                            {profileService.getProfileLabel(profile.name)}
                            {profile.system && (
                              <Badge variant="secondary">
                                <Lock className="w-3 h-3 mr-1" />
                                Sistema
                              </Badge>
                            )}
                          </div>
                          <div className="font-mono text-xs text-gray-500">
                            {profile.name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-md">
                            {profileMenus.length > 0 ? (
                              profileMenus.map((link) => (
                                <Badge
                                  key={link}
                                  variant="outline"
                                  className="bg-amber-50 text-amber-800 border-amber-200"
                                >
                                  {menuLabels[link] || link}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-sm text-gray-400">
                                Nenhum menu
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {countUsers(profile)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(profile)}
                              disabled={
                                !canEdit(profile) ||
                                loadingProfileId === profile.id
                              }
                              title={
                                canEdit(profile)
                                  ? undefined
                                  : "Somente o proprietário do sistema (ROLE_OWNER) pode alterar perfis"
                              }
                            >
                              {loadingProfileId === profile.id ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <Edit3 className="w-4 h-4 mr-1" />
                              )}
                              Editar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteClick(profile)}
                              disabled={!!deleteBlock}
                              title={deleteBlock || undefined}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Excluir
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ProfileFormModal
        open={showModal}
        onOpenChange={setShowModal}
        profile={editingProfile}
        menus={menus}
        onSave={handleSave}
        loading={saving}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o perfil{" "}
              <strong>{deletingProfile?.name}</strong>? Esta ação não pode ser
              desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setDeletingProfile(null)}
              disabled={deleting}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
            >
              {deleting ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
